import {createStore, filterNil} from '@ngneat/elf';
import {deleteEntities, selectEntity, selectManyByPredicate, updateEntities, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, finnFraLokallagerBasertPåForelderId, forelderIdPredikat, foreldreIdPredikat, håndterRequestfeilViaCatchError, leggTilForelderId, opprettEllerOppdatertHendelse, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, slettAlleEntiteterMedForelder, slettAlleEntiteterMedForeldre, slettHendelse, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementId, DokumentId, DokumentRessursClient, DokumentVersjonId, DokumentVersjonModell, FavorittType, ImporterGoogleDokumentISakSpesifikasjon, ImporterMicrosoftOneDriveDokumentISakSpesifikasjon, Modelloppdatering, ModelloppdateringType, MøteId, OppdateringmodellNyttSaksdokument, OppdateringmodellSaksdokumentSlettet, OpprettArkivdokumentISakSpesifikasjon, SakRessursClient, SaksdokumentModell, SakSkyfilOpplastingRessursClient, SkjermingDetaljerModell, SkjermingModell, SpørreundersøkelseId} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, MonoTypeOperatorFunction, Observable, switchMap} from "rxjs";
import {HttpEvent, HttpEventType, HttpResponse} from "@angular/common/http";
import {map, tap} from "rxjs/operators";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sorterSaksdokument} from "felles-ng2/modell/dokumentModellHjelpere";
import {formDataMedFil} from "felles-ng2/støttefunksjoner/dom";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {DataOgMeta, kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MøteMedAggregaterDatatjeneste} from "felles-ng2/tjenester/datalager/MøteMedAggregaterDatatjeneste";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {somEntitetId} from "felles-ng2/modell/modellHjelpere";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";

const debug = initDebugger('SaksdokumentDatatjeneste');


/** Datalager for {@link SaksdokumentModell}.
 * Merk at vi bruker {@link SaksdokumentModell#id} (DokumentVersjonId) som nøkkel, og ikke {@link SaksdokumentModell#dokumentId} (DokumentId). */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class SaksdokumentDatatjeneste {

    /** Lokalt datalager for {@link SaksdokumentModell} entiteter. Merk at {@link SaksdokumentModell#dokumentId} ({@link DokumentId}) benyttes som id i lageret.
     * Bruker {@link AgendaelementId} som forelder.*/
      //public for testbarhet
      //@todo burde versjonsid vært brukt som id? hva skjer når vi laster multiple versjoner av et dokument?
    readonly saksdokumentLager = createStore({name: 'saksdokument'}, withEntities<SaksdokumentModell, 'dokumentId'>({idKey: 'dokumentId'}));

    /** Holder rede på åpne subscriptions basert på dokument.dokumentId. Benyttes ifbm modelloppdateringer. */
    readonly saksdokumentAbonnenter = new DatalagerAbonnenter<DokumentId>('saksdokument'); //public for testbarhet

    /** Holder rede på åpne subscriptions basert på sakId. Benyttes ifbm modelloppdateringer. */
    readonly sakAbonnenter = new DatalagerAbonnenter<AgendaelementId>('saksdokument.sak'); //public for testbarhet

    // datatjenestene under må settes etter opprettelse av instans pga sirkularitet
    private sakDatatjeneste!: SakDatatjeneste;

    constructor(private dokumentRessurs: DokumentRessursClient,
                private sakSkyfilOpplastingRessurs: SakSkyfilOpplastingRessursClient,
                private sakRessurs: SakRessursClient,
                private kommentarDatatjeneste: KommentarDatatjeneste,
                private møteMedAggregaterDatatjeneste: MøteMedAggregaterDatatjeneste,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdatering: Modelloppdateringer) {

        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(sakDatatjeneste: SakDatatjeneste) {
        this.sakDatatjeneste = sakDatatjeneste;
    }

    /** Returnerer saksdokumenter tilknyttet angitt sakId */
    finnTilknyttetSakFraLokallager(sakId: AgendaelementId): Observable<SaksdokumentModell[]> {
        debug('finnTilknyttetSakFraLokallager', sakId, this.sakAbonnenter.antallAbonnenter(sakId))
        return this.sakAbonnenter.observer(sakId,
          this.saksdokumentLager.pipe(
            selectManyByPredicate(forelderIdPredikat(sakId)),
            sorterLevertSamling(sorterSaksdokument)
          ));
    }

    /** Returnerer saksdokumenter tilknyttet angitt sakId, returnerer en liste og ikke en observable, dvs returnerer basert på 'nå'-tilstanden i saksdokumentLager */
    finnTilknyttetSakFraLokallagerSomListe(sakId: AgendaelementId): SaksdokumentModell[] {
        debug('finnTilknyttetSakFraLokallager', sakId, this.sakAbonnenter.antallAbonnenter(sakId))
        return finnFraLokallagerBasertPåForelderId(this.saksdokumentLager, sakId)
    }

    /** Returnerer saksdokumenter tilknyttet angitte sakIder */
    finnTilknyttetSakerFraLokallager(sakIder: AgendaelementId[]): Observable<SaksdokumentModell[]> {
        debug('finnTilknyttetSakerFraLokallager', sakIder)
        return this.sakAbonnenter.observerMange(sakIder,
          this.saksdokumentLager.pipe(
            selectManyByPredicate(foreldreIdPredikat(sakIder)),
            sorterLevertSamling(sorterSaksdokument)
          ));
    }

    /** Henter gjeldende versjon av angitt saksdokument */
    hentMedMeta(id: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<SaksdokumentModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.dokumentRessurs.hentGjeldendeSaksdokument(id),
            registrerILager: respons => {
                this.saksdokumentLager.update(upsertEntities(respons.body!));
                return respons.body!
            },
            hentFraLager: () => this.saksdokumentLager.pipe(selectEntity(id), filterNil()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente saksdokument', 'hentMedMeta')
        });
    }

    /** Henter gjeldende versjon av angitt saksdokument */
    hent(id: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<SaksdokumentModell> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    hentFraFraLokallagerSomObservable(id: DokumentId): Observable<SaksdokumentModell> {
        debug('hentFraFraLokallagerSomObservable', id)
        return this.saksdokumentAbonnenter.observer(id,
          this.saksdokumentLager.pipe(
            selectEntity(id),
            filterNil(),
            distinctUntilChangedDeep())); //kun lever verdier fra lageret ved reelle endringer;
    }

    /** Laster gjeldende versjon av angitt saksdokument fra server og oppdaterer lokalt datalager med dette. */
    async relast(id: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(id, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    /** Markerer angitt dokument som lest i lokalt datalagere */
    markerSomLestILokallager(id: DokumentId) {
        this.saksdokumentLager.update(updateEntities(id, {erLestAv: true}));
    }

    /** Oppdaterer angitt saksdokument i lokallager med angitt skjermings-informasjon. */
    oppdaterSkjermingILokallager(id: DokumentId, skjerming: SkjermingModell) {
        this.oppdaterILokallager(id, {skjerming: skjerming});
    }

    /** Oppdaterer angitt dokument i lokallager med verdier angitt i `oppdatering`. */
    oppdaterILokallager(id: DokumentId, oppdatering: Partial<SaksdokumentModell>) {
        this.saksdokumentLager.update(updateEntities(id, oppdatering));
    }

    /** Erstatter alle dokumenter knytte til angitt sakId i lokallager */
    erstattDokumenterILokallager(dokumenter: SaksdokumentModell[], sakId: AgendaelementId) {
        erstatteAlleEntiteterMedForelder(this.saksdokumentLager, dokumenter, sakId);
    }

    /** Sletter alle dokumenter knytte til angitt sakId i lokallager */
    slettDokumenterTilknyttetSakILokallager(sakId: AgendaelementId) {
        slettAlleEntiteterMedForelder(this.saksdokumentLager, sakId);
    }

    /** Sletter alle dokumenter knyttet til angitte sakIder i lokallager */
    slettDokumenterTilknyttetSakerILokallager(sakIder: AgendaelementId[]) {
        slettAlleEntiteterMedForeldre(this.saksdokumentLager, sakIder);
    }

    /** Registrerer dokument som brukers favoritt dersom ikke allerede markert som favoritt,
     * eller fjernes angitt dokument som brukers favoritt  */
    async endreFavorittmarkering(dokumentversjon: DokumentVersjonModell): Promise<void> {
        //ved markering av dokument som favoritt bruker vi dokumentId og ikke dokumentversjonId
        const favoritterOppdatert = await this.favorittDatatjeneste.endreFavorittmarkering({id: dokumentversjon.dokumentId, favoritter: dokumentversjon.favoritter}, FavorittType.dokument);
        this.oppdaterILokallager(dokumentversjon.dokumentId, {favoritter: favoritterOppdatert});
    }

    /** Registrerer nytt saksdokument. Merk at returnert Observable leverer multiple verdier,
     * typisk vil man være interessert i HttpProgressEvent som inneholder informasjon om hvor langt opplastingen har kommet
     * og HttpResponse som vil inneholder det opprettede SaksdokumentModell */
    registrerSaksdokument(sakId: AgendaelementId, fil: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<SaksdokumentModell>> {
        return this.sakRessurs.opprettDokument$PUT$sak_id_opprettDokument(sakId, formDataMedFil(fil), {rapporterProgresjon: true}).råEventer()
          .pipe(
            this.registrerNyttSaksdokumentMedProgresjonseventer(),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerSaksdokument'))
          );
    }

    /** Registrerer ny saksdokument-versjon. Merk at returnert Observable leverer multiple verdier,
     * typisk vil man være interessert i HttpProgressEvent som inneholder informasjon om hvor langt opplastingen har kommet
     * og HttpResponse som vil inneholder det opprettede SaksdokumentModell */
    registrerSaksdokumentVersjon(sakId: AgendaelementId, dokumentId: DokumentId, fil: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<SaksdokumentModell>> {
        return this.sakRessurs.opprettDokumentVersjon(sakId, dokumentId, formDataMedFil(fil), {rapporterProgresjon: true}).råEventer()
          .pipe(
            this.registrerNyttSaksdokumentMedProgresjonseventer(),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere ny dokumentversjon')), 'registrerSaksdokumentVersjon'))
          );
    }

    async registrerAgendadokument(sakId: AgendaelementId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettAgendaDokument(sakId).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerAgendadokument')
        }));
    }

    async registrerForfalteOppgaverdokument(sakId: AgendaelementId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettForfalteOppgaverDokument(sakId).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerForfalteOppgaverdokument')
        }));
    }

    async registrerProtokolldokumentForMoete(sakId: AgendaelementId, møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettProtokollDokumentISak(sakId, møteId).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerProtokolldokumentForMoete')
        }));
    }

    async registrerProtokolldokumenterForMoete(sakId: AgendaelementId, møteIder: MøteId[], feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettProtokollDokumenterISak(sakId, {moeteIder: møteIder}).råRespons()
              .pipe(tap(respons => {
                  const nyeDokumenter = respons.body!;
                  nyeDokumenter.forEach(nyttDokument => registrerEntitetMedForelder(this.saksdokumentLager, nyttDokument, this.forelderIdFraDokument(nyttDokument)));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerProtokolldokumenterForMoete')
        }));
    }

    async registrerSpørreundersøkelserapporter(sakId: AgendaelementId, undersøkelseIder: SpørreundersøkelseId[], feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettSpørreundersøkelseRapportISak(sakId, {spoerreundersoekelseIder: undersøkelseIder}).råRespons()
              .pipe(tap(respons => {
                  const nyeDokumenter = respons.body!;
                  nyeDokumenter.forEach(nyttDokument => registrerEntitetMedForelder(this.saksdokumentLager, nyttDokument, this.forelderIdFraDokument(nyttDokument)));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerSpørreundersøkelserapporter')
        }));
    }

    async registrerArkivdokument(sakId: AgendaelementId, spesifikasjon: OpprettArkivdokumentISakSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.opprettArkivDokumentISak(sakId, spesifikasjon).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerArkivdokument')
        }));
    }

    async registrerGoogleDokumentISak(id: AgendaelementId, spesifikasjon: ImporterGoogleDokumentISakSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SaksdokumentModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakSkyfilOpplastingRessurs.importerGoogleDokumentISak(id, spesifikasjon).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerGoogleDokumentISak')
        }));
    }

    registrerGoogleDokumentSomDokumentversjon(id: AgendaelementId, dokumentId: DokumentId, spesifikasjon: ImporterGoogleDokumentISakSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SaksdokumentModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakSkyfilOpplastingRessurs.importerGoogleDokumentSomDokumentversjon(id, dokumentId, spesifikasjon).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerGoogleDokumentSomDokumentversjon')
        }));
    }

    registrerMicrosoftOneDriveDokumentISak(id: AgendaelementId, spesifikasjon: ImporterMicrosoftOneDriveDokumentISakSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SaksdokumentModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakSkyfilOpplastingRessurs.importerMicrosoftOneDriveDokumentISak(id, spesifikasjon).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerMicrosoftOneDriveDokumentISak')
        }));
    }

    registrerMicrosoftOneDriveSomDokumentversjon(id: AgendaelementId, dokumentId: DokumentId, spesifikasjon: ImporterMicrosoftOneDriveDokumentISakSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SaksdokumentModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakSkyfilOpplastingRessurs.importerMicrosoftOneDriveSomDokumentversjon(id, dokumentId, spesifikasjon).råRespons()
              .pipe(this.registrerNyttSaksdokument()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt saksdokument')), 'registrerMicrosoftOneDriveSomDokumentversjon')
        }));
    }

    async slettSaksdokument(sakId: AgendaelementId, dokumentId: DokumentId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.slettDokument(sakId, dokumentId).råRespons()
              .pipe(
                tap(respons => this.saksdokumentLager.update(deleteEntities(dokumentId))),
              ),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette dokument')), 'slettSaksdokument')
        }));
    }

    //hjelpemetoder

    private forelderIdFraDokument(dokument: SaksdokumentModell): AgendaelementId {
        return dokument.foreldre.sakId!;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private registrerNyttSaksdokumentMedProgresjonseventer(): MonoTypeOperatorFunction<HttpEvent<SaksdokumentModell>> {
        return tap<HttpEvent<SaksdokumentModell>>(event => {
            if (event.type == HttpEventType.Response) {
                const nyttDokument = event.body!;
                registrerEntitetMedForelder(this.saksdokumentLager, nyttDokument, this.forelderIdFraDokument(nyttDokument));
            }
        });
    }

    private registrerNyttSaksdokument(): MonoTypeOperatorFunction<HttpResponse<SaksdokumentModell>> {
        return tap(respons => {
            const nyttDokument = respons.body!;
            registrerEntitetMedForelder(this.saksdokumentLager, nyttDokument, this.forelderIdFraDokument(nyttDokument));
        });
    }

    /** Ved endringer i dokument må lager som håndterer data inkludert i dokument oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.saksdokumentLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer saksdokumentLager ${hendelse.type} til relatert datatjenester (møteAgr) for dokumentene ${hendelse.ids}`, hendelse, entiteter);
            entiteter
              .forEach(async dokument => {
                  await this.propagerEndringerTilMøteMedAggregaterDatatjeneste(dokument);
              });
        });

        slettHendelse(this.saksdokumentLager).subscribe(hendelse => {
            debug(`propagerer saksdokumentLager ${hendelse.type} til relatert datatjenester (kom/møteAgr) for dokumentene ${hendelse.ids}`);
            this.kommentarDatatjeneste.slettKommentarerTilknyttetForelderILokallager(hendelse.ids);
            //kan ikke propagere endring til møteMedAggregaterDatatjeneste - mangler info om møteId
        });
    }

    /** propager endring i angitt dokument til møteMedAggregaterDatatjeneste for å holde tellere i MøteMedAggregaterModell i synk */
    private async propagerEndringerTilMøteMedAggregaterDatatjeneste(dokument: SaksdokumentModell) {
        const møteId = this.sakDatatjeneste.hentFraLokallager(dokument.foreldre.sakId!)?.moete.id ?? null;
        if (erNil(møteId)) return;

        const alleDokumenter = await firstValueFrom(this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(møteId).pipe(
          map(saker => saker.map(somEntitetId)),
          switchMap(sakIder => this.finnTilknyttetSakerFraLokallager(sakIder))))
        this.møteMedAggregaterDatatjeneste.oppdaterAntallDokumenterIILokallager(møteId, alleDokumenter);
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNyttDokument();
        this.lyttTilDokumentSlettet();
        //når dokument-kortvisning/listevisning tas i bruk for sak må følgende inkluderes
        //se ArkivDatatjeneste for mulig implementasjon
        // this.lyttTilDokumentKonvertertTilPdf();
        // this.lyttTilForsidebildeOpprettet();
        // this.lyttTilDokumentTilSignering();
        // this.lyttTilDokumentKomplettSignert();
        // this.lyttTilDokumentSigneringKanselert();
    }

    private lyttTilNyttDokument() {
        this.opprettetEllerOppdatertSaksdokumentHendelse<OppdateringmodellNyttSaksdokument>(ModelloppdateringType.NYTT_DOKUMENT)
          .medAbonnentoppslag(this.saksdokumentAbonnenter, hendelseModell => hendelseModell.dokumentId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Nytt dokument "{dokument}" registrert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.dokumentId)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilDokumentSlettet() {
        this.slettetHendelse<OppdateringmodellSaksdokumentSlettet>(ModelloppdateringType.DOKUMENT_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Dokumentet "{dokument}" har blitt slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.dokumentId)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedDokument>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, SaksdokumentModell>(this.saksdokumentLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.dokumentId);
    }

    private opprettetEllerOppdatertSaksdokumentHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedDokumentOgVersjon>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, SaksdokumentModell>(this.saksdokumentLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.dokumentId)
          .medEntitetLaster(hendelseModell => this.dokumentRessurs.hentGjeldendeSaksdokument(hendelseModell.dokumentId).råRespons())
          .medForberedLageroppdatering(dokument => leggTilForelderId(dokument, this.forelderIdFraDokument(dokument)));
    }

    /** returnerer objekt med 'dokument' og 'bruker' felt populert fra angitt oppdatering  */
    private i18nArgumenter(oppdatering: Modelloppdatering<any>, dokumentId: DokumentId) {
        return i18nArgumenterMedAnsvarligBruker(oppdatering, {dokument: this.dokumentFilnavn(dokumentId)});
    }

    /** Returnerer dokument.filnavn for dokument registrert i dokumentLager, '...' dersom saken ikke finnes i sakLager */
    private dokumentFilnavn(dokumentId: DokumentId) {
        return this.saksdokumentLager.value.entities[dokumentId]?.filnavn ?? '...';
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }
}

type OppdateringmodellMedDokument = { dokumentId: DokumentId; };

type OppdateringmodellMedDokumentOgVersjon = { dokumentId: DokumentId; dokumentVersjonId: DokumentVersjonId; };
